import L from "leaflet";

export type CanvasLabelLine = {
  text: string;
  zoomLevel: number; // show when mapZoom > zoomLevel (same semantics as LabelsMarker)
  fontSizePx?: number; // optional per line override (fallback to row.fontSizePx)
  fontWeight?: number; // optional per line override (fallback to row.fontWeight)
  color?: string; // optional per line override (fallback to row.color)
};

export type CanvasLabelDatum = {
  id: string;
  latlng: L.LatLngExpression;
  // what to draw
  lines: CanvasLabelLine[];
  // show rules
  minZoom: number;
  // style
  fontSizePx: number;
  fontWeight?: number; // 400 / 700
  color?: string; // default black
  backgroundAlpha?: number; // 0..1
  paddingPx?: number; // default 3
  lineGapPx?: number; // default 2
  borderRadiusPx?: number; // default 0.. (rounded rect)
  // Rotation in radians (for line labels) (TODO: implement and test this)
  angleRad?: number;
  // Shift whole label down (CSS px)
  offsetYPx?: number;
};

type HitBox = { id: string; x0: number; y0: number; x1: number; y1: number };

type Options = {
  pane?: string;
  /**
   * If true, the canvas remains visible during zoom (it scales with the pane transform).
   * If false, we hide it during zoom and only show after the debounced zoomend redraw.
   */
  showDuringZoom?: boolean;
  /**
   * Debounce window for consecutive wheel zooms before doing the final crisp redraw.
   * Typical: 120–200ms.
   */
  zoomEndDebounceMs?: number;
  /**
   * Opacity used while zooming when showDuringZoom=true (to reduce perceived blur).
   * Set to 1 to disable.
   */
  zoomingOpacity?: number;
  onClickId?: (id: string) => void;
};

export default function CanvasLabelLayer(options?: Options) {
  const paneName = options?.pane ?? "labelsPane";
  const showDuringZoom = options?.showDuringZoom ?? true;
  const zoomEndDebounceMs = options?.zoomEndDebounceMs ?? 140;
  const zoomingOpacity = options?.zoomingOpacity ?? 0.9;

  const CanvasLayer = (L.Layer as any).extend({
    
    initialize() {
      this._map = null as L.Map | null;
      this._canvas = null as HTMLCanvasElement | null;
      this._ctx = null as CanvasRenderingContext2D | null;
      this._data = new Map<string, CanvasLabelDatum>();
      this._dpr = 1 as number;
      this._topLeft = null as L.Point | null;
      this._isZooming = false as boolean;
      this._zoomAnimRaf = 0 as number;

      this._zoomEndTimer = 0 as any;
      this._zoomSeq = 0 as number;
      this._zoomEndDebounceMs = zoomEndDebounceMs as number;

      this._hitBoxes = [] as HitBox[];
      this._onClickId = options?.onClickId;
    },

    onAdd: function (map: L.Map) {
      this._map = map;

      const canvas = L.DomUtil.create(
        "canvas",
        "leaflet-canvas-label-layer leaflet-zoom-animated",
      ) as HTMLCanvasElement;

      canvas.style.position = "absolute";
      canvas.style.top = "0";
      canvas.style.left = "0";
      canvas.style.pointerEvents = "none";
      canvas.style.opacity = "1";

      this._canvas = canvas;
      this._ctx = canvas.getContext("2d", { alpha: true });

      const paneEl = map.getPane(paneName) ?? map.getPanes().overlayPane;
      paneEl.appendChild(canvas);

      this._reset();
      this._redraw();

      map.on("moveend", this._onMoveEnd, this);
      map.on("zoomstart", this._onZoomStart, this);
      map.on("zoomend", this._onZoomEnd, this);
      map.on("resize", this._onResize, this);
      map.on("viewreset", this._onViewReset, this);

      if (this._onClickId) {
        L.DomEvent.on(this._canvas, "click", this._onClick, this);
      }
    },

    onRemove: function (map: L.Map) {
      map.off("moveend", this._onMoveEnd, this);
      map.off("zoomstart", this._onZoomStart, this);
      map.off("zoomend", this._onZoomEnd, this);
      map.off("resize", this._onResize, this);
      map.off("viewreset", this._onViewReset, this);

      if (this._zoomAnimRaf) cancelAnimationFrame(this._zoomAnimRaf);
      this._zoomAnimRaf = 0;

      if (this._zoomEndTimer) clearTimeout(this._zoomEndTimer);
      this._zoomEndTimer = 0;

      if (this._canvas) L.DomUtil.remove(this._canvas);

      if (this._onClickId) {
        L.DomEvent.off(this._canvas, "click", this._onClick, this);
      }

      this._map = null;
      this._canvas = null;
      this._ctx = null;
      this._topLeft = null;
      this._data.clear();
    },

    setData: function (rows: CanvasLabelDatum[]) {
      this._data.clear();
      for (const r of rows) this._data.set(r.id, r);
      this._reset();
      this._redraw();
    },

    upsert: function (row: CanvasLabelDatum) {
      this._data.set(row.id, row);
      if (this._isZooming || this._zoomEndTimer) return;
      this._reset();
      this._redraw();
    },

    removeId: function (id: string) {
      this._data.delete(id);
      if (this._isZooming || this._zoomEndTimer) return;
      this._reset();
      this._redraw();
    },

    clear: function () {
      this._data.clear();
      if (this._isZooming || this._zoomEndTimer) return;
      this._reset();
      this._redraw();
    },

    // ---- events ----
    _onClick: function (ev: MouseEvent) {
      if (!this._map || !this._canvas || !this._onClickId) return;

      // mouse position relative to canvas (CSS pixels)
      const rect = this._canvas.getBoundingClientRect();
      const x = ev.clientX - rect.left;
      const y = ev.clientY - rect.top;

      // last-drawn hitboxes are already in canvas-local coords (CSS px)
      for (let i = this._hitBoxes.length - 1; i >= 0; i--) {
        const hb = this._hitBoxes[i];
        if (x >= hb.x0 && x <= hb.x1 && y >= hb.y0 && y <= hb.y1) {
          this._onClickId(hb.id);
          L.DomEvent.stop(ev);
          return;
        }
      }
    },

    _onMoveEnd: function () {
      if (this._isZooming || this._zoomEndTimer) return;
      this._reset();
      this._redraw();
    },

    _onZoomStart: function () {
      if (!this._map || !this._canvas) return;

      this._isZooming = true;

      // cancel any pending "final" redraw from prior zoomend (consecutive wheel zoom)
      if (this._zoomEndTimer) {
        clearTimeout(this._zoomEndTimer);
        this._zoomEndTimer = 0;
      }
      this._zoomSeq++;

      if (showDuringZoom) {
        // keep visible; optionally reduce blur with a slight fade
        this._canvas.style.opacity = String(zoomingOpacity);
      } else {
        // hide completely during zoom
        this._canvas.style.opacity = "0";
      }

      this._canvas.style.willChange = "transform, opacity";

      // follow top-left so the internal origin doesn't drift during the zoom transform
      this._startFollowingTopLeftDuringZoom();
    },

    _onZoomEnd: function () {
      if (!this._map || !this._canvas) return;

      if (this._zoomAnimRaf) cancelAnimationFrame(this._zoomAnimRaf);
      this._zoomAnimRaf = 0;

      const seqAtSchedule = ++this._zoomSeq;

      this._zoomEndTimer = setTimeout(() => {
        if (seqAtSchedule !== this._zoomSeq) return;

        this._zoomEndTimer = 0;
        this._isZooming = false;

        this._reset();
        this._redraw();

        this._canvas!.style.willChange = "auto";
        this._canvas!.style.opacity = "1";
      }, this._zoomEndDebounceMs);
    },

    _onResize: function () {
      if (this._isZooming || this._zoomEndTimer) return;
      this._reset();
      this._redraw();
    },

    _onViewReset: function () {
      if (this._isZooming || this._zoomEndTimer) return;
      this._reset();
      this._redraw();
    },

    _startFollowingTopLeftDuringZoom: function () {
      if (!this._map || !this._canvas) return;

      if (this._zoomAnimRaf) cancelAnimationFrame(this._zoomAnimRaf);

      const tick = () => {
        if (!this._isZooming || !this._map || !this._canvas) {
          this._zoomAnimRaf = 0;
          return;
        }

        const topLeft = this._map.containerPointToLayerPoint([0, 0]);
        this._topLeft = topLeft;
        L.DomUtil.setPosition(this._canvas, topLeft);

        this._zoomAnimRaf = requestAnimationFrame(tick);
      };

      this._zoomAnimRaf = requestAnimationFrame(tick);
    },

    // ---- canvas positioning + sizing ----
    _setDpr: function () {
      this._dpr = Math.max(1, window.devicePixelRatio || 1);
    },

    _reset: function () {
      if (!this._map || !this._canvas || !this._ctx) return;

      const topLeft = this._map.containerPointToLayerPoint([0, 0]);
      this._topLeft = topLeft;
      L.DomUtil.setPosition(this._canvas, topLeft);

      this._setDpr();
      const size = this._map.getSize();

      this._canvas.style.width = `${size.x}px`;
      this._canvas.style.height = `${size.y}px`;

      this._canvas.width = Math.max(1, Math.round(size.x * this._dpr));
      this._canvas.height = Math.max(1, Math.round(size.y * this._dpr));

      this._ctx.setTransform(this._dpr, 0, 0, this._dpr, 0, 0);
    },

    // ---- drawing ----
    _redraw: function () {
      if (!this._map || !this._canvas || !this._ctx || !this._topLeft) return;

      const ctx = this._ctx as CanvasRenderingContext2D;
      const size = this._map.getSize();
      const zoom = this._map.getZoom();

      ctx.clearRect(0, 0, size.x, size.y);
      this._hitBoxes.length = 0;

      const bounds = this._map.getBounds().pad(0.15);

      for (const row of this._data.values() as Iterable<CanvasLabelDatum>) {
        if (zoom < row.minZoom) continue;

        const ll = L.latLng(row.latlng);
        if (!bounds.contains(ll)) continue;

        const activeLines = (row.lines || [])
          .filter((l) => !!l && !!l.text && zoom > l.zoomLevel) // LabelsMarker semantics
          .slice()
          .sort((a, b) => a.zoomLevel - b.zoomLevel);

        if (activeLines.length === 0) continue;

        const layerPt = this._map.latLngToLayerPoint(ll);
        const pt = layerPt.subtract(this._topLeft);

        const pad = row.paddingPx ?? 3;
        const gap = row.lineGapPx ?? 2;
        const bgA = row.backgroundAlpha ?? 0;
        const rad = row.borderRadiusPx ?? 0;

        ctx.textBaseline = "top";

        // --- measure max width + per-line heights (supports per-line fontSizePx) ---
        let maxW = 0;
        const lineHeights: number[] = [];

        for (const l of activeLines) {
          const fs = l.fontSizePx ?? row.fontSizePx ?? 11;
          const fw = l.fontWeight ?? row.fontWeight ?? 400;
          ctx.font = `${fw} ${fs}px sans-serif`;
          maxW = Math.max(maxW, ctx.measureText(l.text).width);
          lineHeights.push(Math.ceil(fs * 1)); // changed from 1,15
        }

        const contentH =
          lineHeights.reduce((sum, h) => sum + h, 0) + gap * Math.max(0, activeLines.length - 1);

        const boxW = Math.ceil(maxW) + pad * 2;
        const boxH = contentH + pad * 2;

        const yShift = row.offsetYPx ?? 0;

        const x0 = Math.round(pt.x - boxW / 2);
        const y0 = Math.round(pt.y - boxH / 2 + yShift);

        // Hitbox (canvas-local, CSS px)
        this._hitBoxes.push({
          id: row.id,
          x0,
          y0,
          x1: x0 + boxW,
          y1: y0 + boxH,
        });

        // Background
        if (bgA > 0) {
          ctx.fillStyle = `rgba(255,255,255,${bgA})`;
          if (rad > 0) {
            const x1 = x0 + boxW;
            const y1 = y0 + boxH;
            const rr = Math.min(rad, boxW / 2, boxH / 2);
            ctx.beginPath();
            ctx.moveTo(x0 + rr, y0);
            ctx.arcTo(x1, y0, x1, y1, rr);
            ctx.arcTo(x1, y1, x0, y1, rr);
            ctx.arcTo(x0, y1, x0, y0, rr);
            ctx.arcTo(x0, y0, x1, y0, rr);
            ctx.closePath();
            ctx.fill();
          } else {
            ctx.fillRect(x0, y0, boxW, boxH);
          }
        }

        // Text (per-line overrides supported)
        let y = y0 + pad;
        for (let i = 0; i < activeLines.length; i++) {
          const l = activeLines[i];
          const fs = l.fontSizePx ?? row.fontSizePx ?? 11;
          const fw = l.fontWeight ?? row.fontWeight ?? 400;
          const color = l.color ?? row.color ?? "black";

          ctx.font = `${fw} ${fs}px sans-serif`;
          ctx.fillStyle = color;
          ctx.fillText(l.text, x0 + pad, y);

          y += lineHeights[i] + gap;
        }
      }
    },
  });

  return new CanvasLayer({}) as L.Layer & {
    setData: (rows: CanvasLabelDatum[]) => void;
    upsert: (row: CanvasLabelDatum) => void;
    removeId: (id: string) => void;
    clear: () => void;
  };
}
